import React from "react"
import {createRoot} from 'react-dom/client'
import axios from "axios"
import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  concat,
} from "@apollo/client"
import { ChakraProvider, defaultSystem } from "@chakra-ui/react"
import { CacheProvider } from "@emotion/react"
import createCache from "@emotion/cache"
import "../../stylesheets/index.scss"

// components
import AccountContainer from "./pages/account/AccountContainer"
import AnthologyContainer from "./pages/anthology/AnthologyContainer"
import ArtistCollabsContainer from "./pages/artist_collabs/ArtistCollabsContainer"
import ArtistResidencyContainer from "./pages/artist_residency/ArtistResidencyContainer"
import ArtistsContainer from "./pages/artists/ArtistsContainer"
import BrowseBetaContainer from "./pages/browse/BrowseBetaContainer"
import BrowseFeaturedMixtapesContainer from "./pages/browse_featured_mixtapes/BrowseFeaturedMixtapesContainer"
import CheckoutContainer from "./pages/checkout/CheckoutContainer"
import ContactContainer from "./pages/contact/ContactContainer"
import CollaborationsContainer from "./pages/collaborations/CollaborationsContainer"
import CustomRequestFormContainer from "./pages/custom_request/CustomRequestFormContainer"
import CareersContainer from "./pages/careers/CareersContainer"
import DownloadSongVersions from "./pages/download_song_versions/DownloadSongVersions"
import GivingBackContainer from "./pages/giving_back/GivingBackContainer"
import HelpContainer from "./pages/help/HelpContainer"
import HomeContainer from "./pages/home/HomeContainer"
import LabelPartnershipsContainer from "./pages/label_partnerships/LabelPartnershipsContainer"
import MixtapesContainer from "./pages/mixtapes/MixtapesContainer"
import MusicLicensingContainer from "./pages/music_licensing/MusicLicensingContainer"
import OrderHistoryContainer from "./pages/order_history/OrderHistoryContainer"
import OurWorkContainer from "./pages/our_work/OurWorkContainer"
import PrismicTestContainer from "./pages/prismic_test/PrismicTestContainer"
import PrismicDevTestContainer from "./pages/prismic_dev_test/PrismicDevTestContainer"
import ProjectSearchContainer from "./pages/project_search/ProjectSearchContainer"
import PurposeContainer from "./pages/purpose/PurposeContainer"
import PrismicPreviewEndpoint from "./pages/prismic_preview/PrismicPreviewEndpoint"
import PrismicDraftContainer from "./pages/prismic_draft/PrismicDraftContainer"
import PodcastLicenseContainer from "./pages/podcast_license/PodcastLicenseContainer"
import ReelLicenseContainer from "./pages/reel_license/ReelLicenseContainer"
import StudioContainer from "./pages/studio/StudioContainer"
import SongsContainer from "./pages/song_version/SongsContainer"
import StyleGuideContainer from "./pages/style_guide/StyleGuideContainer"
import SubmissionsFormContainer from "./pages/submissions/SubmissionsFormContainer"
import SelectBlanketOrderModal from "./shared/components/SelectBlanketOrderModal"
import TeamContainer from "./pages/team/TeamContainer"
import WhatWeDoContainer from "./pages/what_we_do/WhatWeDoContainer"
import WeddingLicenseContainer from "./pages/wedding_license/WeddingLicenseContainer"
import ArtistPageContainer from "./pages/artist/ArtistPageContainer"
import SongVersionsContainer from "./pages/song_version/SongVersionsContainer"
import SongVersionPageContainer from "./pages/song_version/SongVersionPageContainer"
import SongVersionPageActions from "./pages/song_version/SongVersionPageActions"
import ReceiptNextSteps from "./pages/receipt/ReceiptNextSteps"
import MixtapePageContainer from "./pages/mixtape/MixtapePageContainer"
import SimilaritySearchContainer from "./pages/similarity_search/SimilaritySearchContainer"
import BCorpContainer from "./pages/bcorp/BCorpContainer"
import PaginationContainer from "./shared/components/PaginationContainer"
import NewArtistFormContainer from "./pages/forms/NewArtistFormContainer"


// --- CSRF and Apollo Client Setup ---
const getCsrfToken = () => {
  const environmentMeta = document.querySelector('meta[name="environment"]');
  const csrfMeta = document.querySelector('meta[name="csrf-token"]');
  const environment = environmentMeta ? environmentMeta.content : '';
  return environment === "test" ? "" : (csrfMeta ? csrfMeta.content : '');
};

let currentCsrfToken = getCsrfToken();

const httpLink = createHttpLink({
  uri: "/api/graphql",
})

let authMiddleware = new ApolloLink((operation, forward) => {
  operation.setContext({
    headers: {
      "X-CSRF-Token": currentCsrfToken,
    },
  })
  return forward(operation)
})

let apolloClient = new ApolloClient({
  link: concat(authMiddleware, httpLink),
  cache: new InMemoryCache(),
})

axios.defaults.headers.common["X-CSRF-Token"] = currentCsrfToken;

// Function to update CSRF token and recreate Apollo client
const updateCsrfTokenAndClient = (newCsrf) => {
  if (currentCsrfToken !== newCsrf) {
    console.log("Updating CSRF token and recreating Apollo Client.");
    currentCsrfToken = newCsrf;
    axios.defaults.headers.common["X-CSRF-Token"] = currentCsrfToken;

    authMiddleware = new ApolloLink((operation, forward) => {
      operation.setContext({
        headers: { "X-CSRF-Token": currentCsrfToken },
      })
      return forward(operation)
    })

    apolloClient = new ApolloClient({
      link: concat(authMiddleware, httpLink),
      cache: new InMemoryCache(),
    })
  }
};

const containers = [
  { component: <AccountContainer />, elementId: "mm-account-container" },
  { component: <AnthologyContainer />, elementId: "mm-anthology-container" },
  { component: <ArtistCollabsContainer />, elementId: "mm-artist-collabs-container" },
  { component: <ArtistResidencyContainer />, elementId: "mm-artist-residency-container" },
  { component: <ArtistsContainer />, elementId: "mm-artists-container" },
  { component: <ArtistPageContainer />, elementId: "mm-artist-page-container" },
  { component: <BCorpContainer />, elementId: "mm-bcorp-container" },
  { component: <BrowseBetaContainer />, elementId: "mm-browse-beta-container" },
  {
    component: <BrowseFeaturedMixtapesContainer />,
    elementId: "mm-browse-featured-mixtapes-container",
  },
  { component: <CheckoutContainer />, elementId: "mm-checkout-container" },
  { component: <ContactContainer />, elementId: "mm-contact-container" },
  { component: <CollaborationsContainer />, elementId: "mm-collaborations-container" },
  { component: <CustomRequestFormContainer />, elementId: "mm-custom-request" },
  { component: <CareersContainer />, elementId: "mm-careers-container" },
  { component: <DownloadSongVersions />, elementId: "mm-download-song-versions" },
  { component: <GivingBackContainer />, elementId: "mm-giving-back-container" },
  { component: <LabelPartnershipsContainer />, elementId: "mm-label-partnerships-container" },
  { component: <HelpContainer />, elementId: "mm-help-container" },
  { component: <HomeContainer />, elementId: "home-new" },
  { component: <MixtapePageContainer />, elementId: "mm-mixtape-page-container" },
  { component: <MixtapesContainer />, elementId: "mm-mixtapes-container" },
  { component: <MusicLicensingContainer />, elementId: "mm-music-licensing-container" },
  { component: <OrderHistoryContainer />, elementId: "mm-order-history-container" },
  { component: <OurWorkContainer />, elementId: "mm-our-work-container" },
  { component: <PrismicTestContainer />, elementId: "mm-prismic-test-container" },
  { component: <PrismicDevTestContainer />, elementId: "mm-prismic-dev-test-container" },
  { component: <PrismicPreviewEndpoint />, elementId: "mm-prismic-preview-endpoint" },
  { component: <PrismicDraftContainer />, elementId: "mm-prismic-draft-container" },
  { component: <ProjectSearchContainer />, elementId: "mm-project-search-container" },
  { component: <PurposeContainer />, elementId: "mm-purpose-container" },
  { component: <PodcastLicenseContainer />, elementId: "mm-podcast-license-container" },
  { component: <ReceiptNextSteps />, elementId: "mm-next-steps-container" },
  { component: <ReelLicenseContainer />, elementId: "mm-reel-license-container" },
  { component: <SongsContainer />, elementId: "mm-similar-songs-container" },
  { component: <SongVersionsContainer />, elementId: "mm-song-versions-container" },
  { component: <SongVersionPageContainer />, elementId: "mm-song-version-page-container" },
  { component: <SongVersionPageActions />, elementId: "mm-song-version-actions" },
  { component: <StudioContainer />, elementId: "mm-studio-container" },
  { component: <StyleGuideContainer />, elementId: "mm-style-guide-container" },
  { component: <SubmissionsFormContainer />, elementId: "mm-submissions" },
  { component: <SelectBlanketOrderModal />, elementId: "mm-select-blanket-order-modal" },
  { component: <TeamContainer />, elementId: "mm-team-container" },
  { component: <WhatWeDoContainer />, elementId: "mm-what-we-do-container" },
  { component: <WeddingLicenseContainer />, elementId: "mm-wedding-license-container" },
  { component: <SimilaritySearchContainer />, elementId: "mm-youtube-similarity-container" },
  {
    component: <NewArtistFormContainer></NewArtistFormContainer>,
    elementId: "mm-new-artist-form-container",
  },
  { component: <PaginationContainer />, elementId: "mm-pagination-container"}
]

// Global registry to track React roots for proper cleanup
const reactRoots = new Map()

// Create a custom Emotion cache that uses our dedicated insertion point
const createEmotionCache = () => {
  // Check if we're in a browser environment and DOM is available
  if (typeof window !== 'undefined' && typeof document !== 'undefined' && document.readyState !== 'loading') {
    try {
      const insertionPoint = document.getElementById('emotion-insertion-point')
      if (insertionPoint) {
        return createCache({
          key: 'marmoset-emotion',
          insertionPoint: insertionPoint
        })
      }
    } catch (error) {
      console.warn('Failed to create Emotion cache with insertion point:', error)
    }
  }

  // Fallback for asset compilation or when insertion point isn't found
  return createCache({ key: 'marmoset-emotion' })
}

// Lazy initialization - create cache only when needed
let emotionCache = null
const getEmotionCache = () => {
  if (!emotionCache) {
    emotionCache = createEmotionCache();
  }
  return emotionCache
}

const resetEmotionCache = () => {
  emotionCache = createEmotionCache();
};

// --- React Component Rendering Logic ---

// Function to render a React component
const renderReactComponent = (reactNode, element) => {
  if (!element) return;

  const existingRoot = reactRoots.get(element);
  if (existingRoot) return;
  
  const root = createRoot(element);
  reactRoots.set(element, root);

  const App = () => (
    <CacheProvider value={getEmotionCache()}>
      <ApolloProvider client={apolloClient}>
        <ChakraProvider value={defaultSystem}>
          {reactNode}
        </ChakraProvider>
      </ApolloProvider>
    </CacheProvider>
  );
  root.render(<App />);
  
};


const unmountAndClearRoots = () => {
  reactRoots.forEach((root, element) => {
    try {
      root.unmount()
    } catch (error) {
      console.warn('Error unmounting React root during cleanup:', error)
    }
  })
  reactRoots.clear()
};

// --- Main React Mounting/Unmounting for Turbo ---
const mountAllReactComponents = () => {
  containers.forEach((container) => {
    if (document.getElementsByClassName("server-rendered-content")[0]) {
      document.getElementsByClassName("server-rendered-content")[0].style.display = "none";
    }
    if (container.elementId === "mm-select-blanket-order-modal") {
      const elements = Array.from(document.querySelectorAll(`#${container.elementId}`));
      elements.forEach((element) => {
        const svid = element.getAttribute("svid");
        renderReactComponent(<SelectBlanketOrderModal svid={svid} />, element);
      });
    } else {
      const element = document.getElementById(container.elementId);
      if (element) {
        renderReactComponent(container.component, element);
      }
    }
  });
};

// --- Global Turbo Event Listeners ---

// This fires on every Turbo visit (NOT on initial page load)
document.addEventListener("turbo:render", () => {
  // Ensure Emotion cache is fresh for new styles
  resetEmotionCache();

  updateCsrfTokenAndClient(getCsrfToken());

  // Clear any previously mounted React roots and mount new ones
  unmountAndClearRoots();
  mountAllReactComponents();

  reattachLinks();

  // Handle active class for browse subnav
  const currentPath = window.location.pathname;
  const browseHeaderLinks = document.querySelector("#browse-header-links");
  if (browseHeaderLinks) {
    browseHeaderLinks.querySelectorAll("li").forEach((li) => {
      const link = li.querySelector("a");
      if (link && currentPath === link.getAttribute("href")) {
        li.classList.add("active");
      } else {
        li.classList.remove("active");
      }
    });
  }
});

// Clean up React roots and Emotion cache before Turbo navigates away
document.addEventListener("turbo:before-visit", () => {
  unmountAndClearRoots();
  // No need to reset emotion cache here, as turbo:load will do it for the next page
});

// Clean up React roots before Turbo caches the page
// This prevents React apps from living in the cache, which can cause issues
document.addEventListener("turbo:before-cache", unmountAndClearRoots);

const reattachLinks = () => {
  const links = document.querySelectorAll('a[data-bypass="true"]');
  links.forEach((link) => {
    // Only reattach if not already attached to avoid multiple listeners
    if (!link._turboBypassAttached) {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        const href = link.getAttribute("href");
        if (href) {
            Turbo.visit(href); 
        }
      });
      link._turboBypassAttached = true; // Mark as attached
    }
  });

  const preventLinks = document.querySelectorAll("a[data-prevent-default]");
  preventLinks.forEach((link) => {
    if (!link._preventDefaultAttached) {
      const handler = (e) => e.preventDefault();
      link.addEventListener("click", handler);
      link.addEventListener("mouseenter", handler);
      link.addEventListener("mouseleave", handler);
      link._preventDefaultAttached = true;
    }
  });
};

// The old `observeDOM` function and its callback are removed, as `turbo:load` handles this.
// The `initial mounting of components` loop is replaced by `mountAllReactComponents` inside `turbo:load`.
// `rerenderPage` is integrated into `renderReactComponent` and the `mountAllReactComponents` strategy.

// This initial call will be overridden by the first turbo:load event if Turbo is active.
document.addEventListener("DOMContentLoaded", () => {
    emotionCache = createEmotionCache();
    mountAllReactComponents();
    reattachLinks();
});